import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, CalendarPlus, ArrowLeft } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { SimpleLocationsSection } from '@/components/sections/simple-locations-section'
import { TableOfContents } from '@/components/sections/table-of-contents'

const tocItems = [
  { id: 'introduction-section', title: 'Preconception Care & Fertility Support' },
  { id: 'who-this-is-for', title: 'Who This Is For' },
  { 
    id: 'how-acupuncture-helps', 
    title: 'How Acupuncture & Chinese Medicine May Help',
    children: [
      { id: 'working-alongside-ivf', title: 'Working Alongside IVF, IUI & Fertility Treatments' },
      { id: 'preconception-care', title: 'Preconception Care' },
    ]
  },
  { id: 'what-to-expect', title: 'What to Expect in a Fertility Appointment' },
  { 
    id: 'emotional-support', 
    title: 'Emotional Support & Nervous System Care',
    children: [
      { id: 'couples-partners', title: 'Couples & Partners' },
    ]
  },
  { id: 'is-this-right', title: 'Is This Approach Right for You?' },
]

export default function FertilityPreconceptionPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const showBackLink = location.state?.fromParent === true
  const showHomepageBackLink = location.state?.fromHomepage === true
  const showAppointmentsBackLink = location.state?.fromAppointmentsPage === true

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Preconception Care & Fertility Support | Raintree Chinese Medicine</title>
        <meta name="description" content="Fertility and preconception care support at Raintree Chinese Medicine. Comprehensive support for natural conception and IVF treatments." />
        <meta property="og:title" content="Preconception Care & Fertility Support | Raintree Chinese Medicine" />
        <meta property="og:description" content="Fertility and preconception care support at Raintree Chinese Medicine. Comprehensive support for natural conception and IVF treatments." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/what-we-help-with/fertility-preconception" />
      </Helmet>
      <h1 className="sr-only">Preconception Care & Fertility Support</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0 relative">
        {showHomepageBackLink && (
          <button
            onClick={() => navigate('/#what-we-help-with-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        {showAppointmentsBackLink && !showHomepageBackLink && (
          <button
            onClick={() => navigate('/appointments#areas-of-focus-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to Appointments and Fees
          </button>
        )}
        {showBackLink && !showHomepageBackLink && !showAppointmentsBackLink && (
          <button
            onClick={() => navigate('/what-we-help-with')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Preconception Care & Fertility Support
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
              Acupuncture and Chinese medicine aim to support and optimise your body as you move through investigations, stimulation cycles, transfers and the waiting periods in between.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('fertility_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('fertility_hero_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Preconception Care & Fertility Support
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                Acupuncture and Chinese medicine aim to support and optimise your body as you move through investigations, stimulation cycles, transfers and the waiting periods in between.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4 w-80">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('fertility_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('fertility_hero_free_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Free 15-minute phone consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="introduction-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              Preconception Care & Fertility Support
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-8 text-left">
                Trying to conceive can be exciting, exhausting, hopeful and heartbreaking – often all in the same week.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                This page is for people who are:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Trying to conceive naturally</li>
                <li>Moving through fertility investigations</li>
                <li>Preparing for, or currently in, IVF/ICSI cycles</li>
                <li>Thinking ahead and wanting to optimise their health before trying for a baby</li>
              </ul>
              <p className="text-gray-600 text-left">
                Renee offers acupuncture and Chinese herbal medicine to support your body and nervous system through the fertility and preconception times, alongside your medical care.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section id="who-this-is-for" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              Who This Is For
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                People often come for fertility and preconception support when they:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Have been trying to conceive for longer than they expected</li>
                <li>Are feeling tired by appointments, tests and information overload</li>
                <li>Are preparing for or moving through IVF/ICSI or other assisted reproductive treatments</li>
                <li>Have irregular, painful or very heavy periods and are wondering how this links with fertility</li>
                <li>Want to give themselves the best chance before trying to conceive, even if they haven't started yet</li>
              </ul>
              <p className="text-gray-600 mb-4 text-left">
                Some people already have a clear diagnosis. Others have been told that "everything looks fine" but still aren't pregnant. Both situations can be stressful in different ways.
              </p>
              <p className="text-gray-600 text-left">
                You don't need to have a specific diagnosis to seek support here.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Acupuncture & Chinese Medicine May Help Section */}
      <section id="how-acupuncture-helps" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              How Acupuncture & Chinese Medicine May Help in Fertility Care
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Within fertility and preconception care, acupuncture and Chinese medicine are used to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Pay closer attention to your cycle over time – length, bleeding, spotting, pain and premenstrual symptoms</li>
                <li>Support stress, sleep and mood during what is often a very emotional process</li>
                <li>Work with digestion and energy levels, which can affect how you feel month to month</li>
                <li>Offer a calm, steady space to process what's happening and plan next steps</li>
              </ul>
              <p className="text-gray-600 text-left">
              Treatment is tailored to where you are in your fertility journey and is always intended to sit <strong>alongside</strong>, not replace, the care you receive from your GP, fertility specialist and IVF clinic.
            </p>
            </div>

          </motion.div>
        </div>
      </section>

      {/* Working Alongside IVF, IUI & Fertility Treatments Section */}
      <section id="working-alongside-ivf" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Working Alongside IVF, IUI & Fertility Treatments
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Many people see Renee while they are:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Moving through IVF or ICSI cycles</li>
                <li>Preparing for frozen embryo transfers</li>
                <li>Having ovulation induction medications or IUI</li>
                <li>Deciding what to do after previous cycles or pregnancy losses</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                In these situations, acupuncture is often timed around:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Preparation cycles</li>
                <li>Stimulation and monitoring phases</li>
                <li>Egg collection and recovery</li>
                <li>Transfer dates</li>
                <li>The "two-week wait"</li>
              </ul>
              <p className="text-gray-600 text-left">
                Renee can work with your treatment timeline and liaise with your existing care if needed, so appointments feel integrated rather than like "one more thing to juggle".
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Preconception Care Section */}
      <section id="preconception-care" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Preconception Care (Before You Start Trying)
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Some people come before they start trying for a baby, or between pregnancies, to give themselves time to rebuild and prepare.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Preconception care may involve:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Regulating or better understanding your cycle</li>
                <li>Working on period-related pain, PMS or other hormonal symptoms</li>
                <li>Supporting recovery after hormonal contraception</li>
                <li>Rebuilding energy, sleep and digestion after stress, illness or previous pregnancies</li>
                <li>Looking at realistic changes to support your health in the months ahead</li>
              </ul>
              <p className="text-gray-600 text-left">
                This can be especially helpful if you are juggling a lot in your life and want a more grounded foundation before actively trying to conceive.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect in a Fertility Appointment Section */}
      <section id="what-to-expect" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              What to Expect in a Fertility Appointment
            </h2>
            
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <div className="mb-8">
                <h3 className="text-xl md:text-2xl mb-4 text-[#3f4141] text-center">
                  Your First Visit
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Your initial consultation and treatment is a longer appointment to give us time to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-left">
                  <li>Talk through your fertility history and timeline so far</li>
                  <li>Map out your menstrual cycles, symptoms and any relevant test results</li>
                  <li>Ask about sleep, digestion, energy and stress levels</li>
                  <li>Clarify where you are with investigations or treatments (or what's planned)</li>
                  <li>Begin acupuncture treatment, and, where appropriate, discuss Chinese herbal medicine and simple lifestyle support</li>
                </ul>
                <p className="text-gray-600 text-left">
                  You won't be asked to decide everything in one session. The aim is to understand your situation clearly and agree on the next few steps together.
                </p>
              </div>

              <div>
                <h3 className="text-xl md:text-2xl mb-4 text-[#3f4141] text-center">
                  Ongoing Care
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Ongoing appointments focus on:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-6 text-left">
                  <li>Checking in on how you've been since the last visit – physically and emotionally</li>
                  <li>Adjusting acupuncture points based on where you are in your cycle or treatment schedule</li>
                  <li>Making small, practical changes that feel manageable, not overwhelming</li>
                  <li>Reviewing how often it makes sense to come in, depending on your goals, stress levels and other care</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Some people come weekly during certain phases (for example around IVF treatment), while others prefer a steadier rhythm, such as fortnightly or monthly. This is something you and Renee will decide together.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Emotional Support & Nervous System Care Section */}
      <section id="emotional-support" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Emotional Support & Nervous System Care
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Fertility and preconception work is rarely just physical.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Many people describe:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Feeling anxious in the lead-up to results or appointments</li>
                <li>Carrying grief or disappointment from past cycles or pregnancy losses</li>
                <li>Feeling like their body has become a project or problem to fix</li>
                <li>Feeling alone, even when they're surrounded by other people</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Sessions are a space to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Slow down and be with what's actually happening</li>
                <li>Feel physically held and more grounded during acupuncture</li>
                <li>Talk things through with someone who understands fertility timelines and treatments</li>
              </ul>
              <p className="text-gray-600 text-left">
                While acupuncture and Chinese medicine are not counselling or psychotherapy, they can sit alongside emotional support and help your nervous system find more stable ground.
            </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Couples & Partners Section */}
      <section id="couples-partners" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Couples & Partners
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                You're very welcome to attend on your own or with a partner.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                In many cases it can be helpful to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Consider your partner's sleep, stress and general health as part of the overall picture</li>
                <li>Look at how both people are coping with the emotional and practical load</li>
                <li>Clarify what support each person needs, rather than everything falling on one person's shoulders</li>
              </ul>
              <p className="text-gray-600 text-left">
                If both partners are seeking acupuncture or Chinese medicine, this can be discussed and planned out at or after the initial appointment.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Is This Approach Right for You? Section */}
      <section id="is-this-right" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Is This Approach Right for You?
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Fertility and preconception support at Raintree may be a good fit if you are looking for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>A steady, experienced practitioner to walk alongside your fertility journey</li>
                <li>Treatment that takes into account hormones, stress, sleep, digestion and emotions as part of one connected picture</li>
                <li>A calm, unhurried space where you can ask questions and feel listened to</li>
                <li>Care that respects and works with your medical and fertility team</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                If you're unsure, you don't have to decide straight away.
              </p>
              <p className="text-gray-600 text-left">
                You're welcome to book a{' '}
              <button
                  onClick={() => openBooking('fertility_approach_free_consult')}
                className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
              >
                  free 15-minute phone consult
              </button>
                {' '}to talk briefly with Renee about your situation and ask any initial questions.
            </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Appointments, Locations & How to Start */}
      <SimpleLocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `fertility_locations_${locationName.toLowerCase()}` })
        openBooking(`fertility_locations_${locationName.toLowerCase()}`)
      }} />

      {/* Next Steps CTA Section */}
      <section className="py-16 lg:py-32 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h2 className="text-2xl md:text-xl mb-10 text-[#3f4141]">
                Next steps
            </h2>
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  For new fertility or preconception patients, the best starting point is:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 inline-block text-left  max-w-lg mx-auto">
                  <li>
                    <strong>Initial Consultation & Treatment</strong> – longer first appointment to take a full history and begin treatment.
                  </li>
                </ul>
                <p className="text-gray-600  max-w-lg mx-auto">
                  You can then decide together how often to continue and which clinic is most practical for you.
                </p>
              </div>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
              <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                onClick={() => {
                  trackEvent('book_appointment_click', { location: 'fertility_cta_view_appointments' })
                  navigate('/appointments')
                }}
              >
                <span className="inline-flex items-center gap-2">
                  Appointment Types & Fees
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Button>
              <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                onClick={() => openBooking('fertility_cta_initial_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                </span>
              </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-gray-600 text-gray-600 hover:bg-gray-50 sm:w-1/2 mx-auto"
                onClick={() => openBooking('fertility_cta_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Title centered above two columns */}
            <div className="hidden lg:block">
              <h2 className="text-3xl lg:text-4xl mb-16 text-[#3f4141] text-center">
                Next steps
              </h2>
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
                <div>
                  <p className="text-gray-600 mb-4 text-base lg:text-md">
                    For new fertility or preconception patients, the best starting point is:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-base lg:text-md">
                    <li>
                      <strong>Initial Consultation & Treatment</strong> – longer first appointment to take a full history and begin treatment.
                    </li>
                  </ul>
                  <p className="text-gray-600 text-base lg:text-md">
                    You can then decide together how often to continue and which clinic is most practical for you.
                  </p>
                </div>
                <div className="flex flex-col gap-3 mx-auto px-4 w-80">
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => {
                      trackEvent('book_appointment_click', { location: 'fertility_cta_view_appointments' })
                      navigate('/appointments')
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      Appointment Types & Fees
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Button>
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('fertility_cta_initial_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Initial Consult & Treatment
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-gray-600 text-gray-600 hover:bg-gray-50"
                    onClick={() => openBooking('fertility_cta_free_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Free 15-minute phone consult
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  )
}

